import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { signUpAsync, clearError } from "../../../store/slices/authSlice";

/**
 * Formulário de cadastro em modal.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
function SignUpForm({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({ 
    nome: '', 
    email: '', 
    password: '', 
    confirmPassword: '',
    logradouro: '',
    numero: '',
    complemento: '',
    cep: '',
    cidade: '',
    estado: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      setTimeout(() => {
        onClose();
        setFormData({ 
          nome: '', 
          email: '', 
          password: '', 
          confirmPassword: '',
          logradouro: '',
          numero: '',
          complemento: '',
          cep: '',
          cidade: '',
          estado: ''
        });
        setErrors({});
      }, 100);
    }
  }, [isAuthenticated, onClose]);

  useEffect(() => {
    if (open) {
      dispatch(clearError());
    }
  }, [open, dispatch]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome) newErrors.nome = 'Nome é obrigatório';
    if (!formData.email) newErrors.email = 'Email é obrigatório';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.password) newErrors.password = 'Senha é obrigatória';
    else if (formData.password.length < 6) newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    if (!formData.logradouro) newErrors.logradouro = 'Logradouro é obrigatório';
    if (!formData.numero) newErrors.numero = 'Número é obrigatório';
    if (!formData.cep) newErrors.cep = 'CEP é obrigatório';
    else if (!/^\d{5}-?\d{3}$/.test(formData.cep)) newErrors.cep = 'CEP inválido';
    if (!formData.cidade) newErrors.cidade = 'Cidade é obrigatória';
    if (!formData.estado) newErrors.estado = 'Estado é obrigatório';
    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    const { confirmPassword, ...userData } = formData;
    
    dispatch(signUpAsync(userData));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Criar Conta
        <IconButton onClick={onClose} size="small">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            name="nome"
            label="Nome Completo"
            fullWidth
            margin="normal"
            value={formData.nome}
            onChange={handleChange}
            error={!!errors.nome}
            helperText={errors.nome}
            disabled={isLoading}
          />
          <TextField
            name="email"
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isLoading}
            autoComplete="email"
          />
          <TextField
            name="password"
            label="Senha"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            disabled={isLoading}
            autoComplete="new-password"
          />
          <TextField
            name="confirmPassword"
            label="Confirmar Senha"
            type="password"
            fullWidth
            margin="normal"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={isLoading}
            autoComplete="new-password"
          />
          <TextField
            name="logradouro"
            label="Logradouro"
            fullWidth
            margin="normal"
            value={formData.logradouro}
            onChange={handleChange}
            error={!!errors.logradouro}
            helperText={errors.logradouro}
            disabled={isLoading}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              name="numero"
              label="Número"
              value={formData.numero}
              onChange={handleChange}
              error={!!errors.numero}
              helperText={errors.numero}
              disabled={isLoading}
              sx={{ flex: 1 }}
            />
            <TextField
              name="complemento"
              label="Complemento"
              value={formData.complemento}
              onChange={handleChange}
              disabled={isLoading}
              sx={{ flex: 2 }}
            />
          </Box>
          <TextField
            name="cep"
            label="CEP"
            fullWidth
            margin="normal"
            value={formData.cep}
            onChange={handleChange}
            error={!!errors.cep}
            helperText={errors.cep}
            disabled={isLoading}
            placeholder="00000-000"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              name="cidade"
              label="Cidade"
              value={formData.cidade}
              onChange={handleChange}
              error={!!errors.cidade}
              helperText={errors.cidade}
              disabled={isLoading}
              sx={{ flex: 2 }}
            />
            <TextField
              name="estado"
              label="Estado"
              value={formData.estado}
              onChange={handleChange}
              error={!!errors.estado}
              helperText={errors.estado}
              disabled={isLoading}
              sx={{ flex: 1 }}
              placeholder="SP"
            />
          </Box>
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Criando...' : 'Criar Conta'}
            </Button>
            <Button onClick={onClose} variant="outlined" fullWidth disabled={isLoading}>
              Cancelar
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default SignUpForm;